import { Injectable } from '@angular/core';

import { Observable, map, startWith } from 'rxjs';

import { FT_EVOUCHERS, QA_MODE_COOKIE_NAME, UserContextService } from '@hdms/shared/data-access';
import { HeaderMode, ModeAmData, ModeClass, ModeEvData, ModeQaData, ModeType } from '@hdms/shared/page-frame/types';
import { CookieService, FeatureToggleService } from '@hdms/shared/util-core';

@Injectable({
  providedIn: 'root'
})
export class HeaderModeService {
  constructor(
    private readonly userContext: UserContextService,
    private readonly cookieService: CookieService,
    private readonly ftService: FeatureToggleService
  ) {}

  public getMode$(): Observable<HeaderMode[]> {
    return this.userContext.update$().pipe(
      startWith(null),
      map(_ =>
        [
          { class: ModeClass.QA, ...this.getQaData() },
          { class: ModeClass.AM, ...this.getEmployeeData() }
        ].filter(m => !!m.type)
      )
    );
  }

  /**
   * Display QA label bar, based on cookie value.
   * Having the bar as part of cached SSR result is fine,
   * as in case QA label is set a dedicated cache key will be used.
   * @private
   */
  private getQaData(): HeaderMode<ModeQaData> {
    const label = this.cookieService.get<string>(QA_MODE_COOKIE_NAME);
    return label ? { type: ModeType.QA, data: { label } } : null;
  }

  /**
   * Display Employee bar, based on cookie and user context.
   * In order to reserve the space, already render the bar based on `SsoUserRole` cookie presence,
   * but add user data later, once user context has been received.
   * Be aware, that user data must not be rendered on server-side
   * in order to keep cached page clean from user data.
   * Having only the empty bar as part of cached SSR result is fine,
   * as for employees a dedicated cache key will be used.
   * @private
   */
  private getEmployeeData(): HeaderMode {
    const isAm = this.userContext.isAccountManager;
    const isEv = this.userContext.isEVoucherBackofficeUser && this.ftService.getToggleById(FT_EVOUCHERS);

    if (isAm || isEv) {
      const modeData: HeaderMode = { type: isAm ? ModeType.AM : ModeType.EV };

      if (!this.userContext.preventUserDataCaching && this.userContext.hasData) {
        modeData.data = isAm ? this.getAmData() : this.getEvData();
      }

      return modeData;
    }

    return null;
  }

  private getAmData(): ModeAmData {
    const { company, company2 } = this.userContext.account.address || {};
    const {
      contact: { firstName, lastName },
      preselectedShipToId
    } = this.userContext;

    return { company, company2, firstName, lastName, preselectedShipToId };
  }

  private getEvData(): ModeEvData {
    const {
      contact: { formatted }
    } = this.userContext;

    return { formatted };
  }
}
